import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Admin({ language }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const texts = {
    zh: {
      loginTitle: '管理员登录',
      username: '用户名',
      password: '密码',
      loginButton: '登录',
      logoutButton: '退出登录',
      loginError: '用户名或密码错误',
      adminTitle: '投稿管理',
      totalSubmissions: '总投稿数',
      exportCSV: '导出CSV',
      noSubmissions: '暂无投稿',
      loading: '加载中...',
      table: {
        id: 'ID',
        forum: '分论坛',
        title: '题目',
        author: '作者',
        email: '邮箱',
        country: '国家/地区',
        submittedAt: '提交时间',
        actions: '操作'
      },
      view: '查看',
      delete: '删除',
      confirmDelete: '确定要删除这条投稿吗？',
      modalTitle: '投稿详情',
      close: '关闭',
      fields: {
        forum: '分论坛',
        title: '题目',
        abstract: '摘要',
        socialScience: '社科类补充',
        keywords: '关键词',
        authorName: '作者姓名',
        affiliation: '单位',
        seniority: '年资',
        email: '邮箱',
        phone: '电话',
        wechat: '微信',
        country: '国家/地区',
        bio: '个人简介',
        submittedAt: '提交时间'
      }
    },
    en: {
      loginTitle: 'Admin Login',
      username: 'Username',
      password: 'Password',
      loginButton: 'Login',
      logoutButton: 'Logout',
      loginError: 'Invalid username or password',
      adminTitle: 'Submission Management',
      totalSubmissions: 'Total Submissions',
      exportCSV: 'Export CSV',
      noSubmissions: 'No submissions yet',
      loading: 'Loading...',
      table: {
        id: 'ID',
        forum: 'Forum',
        title: 'Title',
        author: 'Author',
        email: 'Email',
        country: 'Country',
        submittedAt: 'Submitted',
        actions: 'Actions'
      },
      view: 'View',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this submission?',
      modalTitle: 'Submission Details',
      close: 'Close',
      fields: {
        forum: 'Forum',
        title: 'Title',
        abstract: 'Abstract',
        socialScience: 'Social Science Supplement',
        keywords: 'Keywords',
        authorName: 'Author Name',
        affiliation: 'Affiliation',
        seniority: 'Academic Level',
        email: 'Email',
        phone: 'Phone',
        wechat: 'WeChat',
        country: 'Country/Region',
        bio: 'Personal Bio',
        submittedAt: 'Submitted At'
      }
    }
  };

  const t = texts[language] || texts.en;

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/verify`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });
      if (response.ok) {
        setToken(tokenToVerify);
        setIsLoggedIn(true);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.token) {
        setToken(data.token);
        setIsLoggedIn(true);
        localStorage.setItem('adminToken', data.token);
        setUsername('');
        setPassword('');
      } else {
        setLoginError(t.loginError);
      }
    } catch (error) {
      setLoginError(t.loginError);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setIsLoggedIn(false);
    localStorage.removeItem('adminToken');
    setSubmissions([]);
  };

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/admin/submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      } else if (response.status === 401 || response.status === 403) {
        handleLogout();
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchSubmissions();
    }
  }, [isLoggedIn, token, fetchSubmissions]);

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/submissions/export/csv`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `submissions_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/submissions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSubmissions(prev => prev.filter(s => s.id !== id));
      }
    } catch (error) {
      console.error('Failed to delete submission:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US');
  };

  // Login form
  if (!isLoggedIn) {
    return (
      <div className="admin-page">
        <div className="admin-login-container">
          <div className="admin-login-card">
            <h1 className="admin-login-title">{t.loginTitle}</h1>
            <form onSubmit={handleLogin} className="admin-login-form">
              <div className="form-group">
                <label className="form-label">{t.username}</label>
                <input
                  type="text"
                  className="form-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.password}</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              {loginError && (
                <div className="login-error">{loginError}</div>
              )}
              <button 
                type="submit" 
                className="admin-login-btn"
                disabled={loading}
              >
                {loading ? '...' : t.loginButton}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1 className="admin-title">{t.adminTitle}</h1>
          <div className="admin-actions">
            <span className="submission-count">
              {t.totalSubmissions}: {submissions.length}
            </span>
            <button 
              className="admin-btn export-btn"
              onClick={handleExportCSV}
              disabled={submissions.length === 0}
            >
              {t.exportCSV}
            </button>
            <button 
              className="admin-btn logout-btn"
              onClick={handleLogout}
            >
              {t.logoutButton}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="admin-loading">{t.loading}</div>
        ) : submissions.length === 0 ? (
          <div className="admin-empty">{t.noSubmissions}</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t.table.id}</th>
                  <th>{t.table.forum}</th>
                  <th>{t.table.title}</th>
                  <th>{t.table.author}</th>
                  <th>{t.table.email}</th>
                  <th>{t.table.country}</th>
                  <th>{t.table.submittedAt}</th>
                  <th>{t.table.actions}</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr key={submission.id}>
                    <td>{submission.id}</td>
                    <td>{submission.forum}</td>
                    <td className="title-cell" title={submission.title}>
                      {submission.title.length > 30 
                        ? submission.title.substring(0, 30) + '...' 
                        : submission.title}
                    </td>
                    <td>{submission.author_name}</td>
                    <td>{submission.author_email}</td>
                    <td>{submission.author_country}</td>
                    <td>{formatDate(submission.submitted_at)}</td>
                    <td className="actions-cell">
                      <button 
                        className="action-btn view-btn"
                        onClick={() => handleViewSubmission(submission)}
                      >
                        {t.view}
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteSubmission(submission.id)}
                      >
                        {t.delete}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {showModal && selectedSubmission && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t.modalTitle}</h2>
                <button 
                  className="modal-close"
                  onClick={() => setShowModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="detail-row">
                  <span className="detail-label">{t.fields.forum}:</span>
                  <span className="detail-value">{selectedSubmission.forum}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t.fields.title}:</span>
                  <span className="detail-value">{selectedSubmission.title}</span>
                </div>
                <div className="detail-row full-width">
                  <span className="detail-label">{t.fields.abstract}:</span>
                  <p className="detail-value abstract-text">{selectedSubmission.abstract}</p>
                </div>
                {selectedSubmission.social_science_enabled === 1 && (
                  <div className="detail-row full-width">
                    <span className="detail-label">{t.fields.socialScience}:</span>
                    <p className="detail-value">{selectedSubmission.social_science_content}</p>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">{t.fields.keywords}:</span>
                  <span className="detail-value">
                    {Array.isArray(selectedSubmission.keywords) 
                      ? selectedSubmission.keywords.join(', ')
                      : selectedSubmission.keywords}
                  </span>
                </div>
                <div className="detail-divider"></div>
                <div className="detail-row">
                  <span className="detail-label">{t.fields.authorName}:</span>
                  <span className="detail-value">{selectedSubmission.author_name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t.fields.affiliation}:</span>
                  <span className="detail-value">{selectedSubmission.author_affiliation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t.fields.seniority}:</span>
                  <span className="detail-value">{selectedSubmission.author_seniority}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t.fields.email}:</span>
                  <span className="detail-value">{selectedSubmission.author_email}</span>
                </div>
                {selectedSubmission.author_phone && (
                  <div className="detail-row">
                    <span className="detail-label">{t.fields.phone}:</span>
                    <span className="detail-value">{selectedSubmission.author_phone}</span>
                  </div>
                )}
                {selectedSubmission.author_wechat && (
                  <div className="detail-row">
                    <span className="detail-label">{t.fields.wechat}:</span>
                    <span className="detail-value">{selectedSubmission.author_wechat}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">{t.fields.country}:</span>
                  <span className="detail-value">{selectedSubmission.author_country}</span>
                </div>
                {selectedSubmission.author_bio && (
                  <div className="detail-row full-width">
                    <span className="detail-label">{t.fields.bio}:</span>
                    <p className="detail-value">{selectedSubmission.author_bio}</p>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">{t.fields.submittedAt}:</span>
                  <span className="detail-value">{formatDate(selectedSubmission.submitted_at)}</span>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="admin-btn"
                  onClick={() => setShowModal(false)}
                >
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
